import { Search, Focus } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { LaunchFilter, LaunchSort } from './filter-launches';

/** Sort dropdown options. */
const SORT_OPTIONS: ReadonlyArray<{ value: LaunchSort; label: string }> = [
  { value: 'default', label: 'Sort: Date' },
  { value: 'provider', label: 'Sort: Provider' },
  { value: 'name', label: 'Sort: Name' },
];

/** Search + provider/orbit/sort controls for the launch lists, plus the unit focus toggle. */
export function MissionFilters({
  filter,
  providers,
  orbits,
  onChange,
  focusConfigured,
  focusActive,
  focusSites,
  hiddenByFocus,
  onToggleFocus,
}: {
  filter: LaunchFilter;
  providers: string[];
  orbits: string[];
  onChange: (next: LaunchFilter) => void;
  focusConfigured: boolean;
  focusActive: boolean;
  /** Focus sites the pool is scoped to server-side (empty when the pool is global). */
  focusSites: string[];
  /** Launches the focus hides client-side — only meaningful over a global pool. */
  hiddenByFocus: number;
  onToggleFocus: () => void;
}): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-echo-border bg-echo-grey/80 p-2">
      {focusConfigured && (
        <button
          onClick={onToggleFocus}
          aria-pressed={focusActive}
          className={cn(
            'flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors',
            focusActive
              ? 'border-echo-yellow/60 bg-echo-yellow/15 text-echo-yellow'
              : 'border-echo-border text-muted-foreground hover:border-white/25 hover:text-foreground',
          )}
        >
          <Focus className="h-3.5 w-3.5" />
          {focusActive ? 'Focused' : 'All launches'}
          {focusActive && focusSites.length > 0 ? (
            // Site-scoped pool: a hidden count is meaningless (the server only
            // returns focus-site launches), so name the scope instead.
            <span
              title={focusSites.join(', ')}
              className="rounded-full bg-black/40 px-1.5 text-[9px] font-bold text-muted-foreground"
            >
              {focusSites.length === 1 ? focusSites[0] : `${focusSites.length} sites`}
            </span>
          ) : (
            focusActive &&
            hiddenByFocus > 0 && (
              <span className="rounded-full bg-black/40 px-1.5 text-[9px] font-bold text-muted-foreground">
                {hiddenByFocus} hidden
              </span>
            )
          )}
        </button>
      )}
      <div className="relative min-w-40 flex-1">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          value={filter.query}
          onChange={(event) => onChange({ ...filter, query: event.target.value })}
          placeholder="Search missions, rockets, sites…"
          className="w-full rounded-md border border-echo-border bg-black/30 py-1.5 pl-8 pr-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-echo-yellow focus:outline-none"
        />
      </div>
      <Dropdown
        value={filter.provider}
        anyLabel="All providers"
        options={providers}
        onChange={(provider) => onChange({ ...filter, provider })}
      />
      <Dropdown
        value={filter.orbit}
        anyLabel="All orbits"
        options={orbits}
        onChange={(orbit) => onChange({ ...filter, orbit })}
      />
      <select
        value={filter.sort}
        onChange={(event) => onChange({ ...filter, sort: event.target.value as LaunchSort })}
        className="rounded-md border border-echo-border bg-black/30 px-2 py-1.5 text-sm text-foreground focus:border-echo-yellow focus:outline-none"
      >
        {SORT_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/** A facet dropdown with an "any" sentinel (empty string) at the top. */
function Dropdown({
  value,
  anyLabel,
  options,
  onChange,
}: {
  value: string;
  anyLabel: string;
  options: string[];
  onChange: (value: string) => void;
}): React.JSX.Element {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="max-w-44 rounded-md border border-echo-border bg-black/30 px-2 py-1.5 text-sm text-foreground focus:border-echo-yellow focus:outline-none"
    >
      <option value="">{anyLabel}</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}
